import type { OperationRun } from '@foodlista/trigger-vue'
import type { OperationItem } from '../types'
import { TERMINAL_RUN_STATUSES } from '@foodlista/trigger-vue'
import { useNow } from '@vueuse/core'
import {
  isOperationActive,
  operationProgress,
  type OperationProgress,
  operationStatus,
  type OperationStatus,
} from '../utils/lifecycle'
import { useRestaurantOperationsContext } from './provideRestaurantOperations'

export function useOperationActive(
  operationId: MaybeRefOrGetter<string | null | undefined>,
) {
  const context = useRestaurantOperationsContext()
  const now = useNow({ interval: 10_000 })
  const opId = computed(() => toValue(operationId) ?? null)

  const item = computed((): OperationItem | null => {
    const id = opId.value
    if (!id) {
      return null
    }

    return context.items.value.find(entry => entry.id === id) ?? null
  })

  const run = computed((): OperationRun | null => {
    const id = opId.value
    if (!id) {
      return null
    }

    return (
      item.value?.run
      ?? context.liveByOperationId.value.get(id)
      ?? null
    )
  })

  const isActive = computed(() => {
    const id = opId.value
    if (!id) {
      return false
    }

    const current = item.value
    const currentRun = run.value

    if (current) {
      return isOperationActive(current, current.run, now.value)
    }

    // Dismiss hides from BOC feed; scoped tracking keeps receiving run updates.
    if (currentRun) {
      return !TERMINAL_RUN_STATUSES.has(currentRun.status)
    }

    return context.watchedOperationIds.value.has(id)
  })

  const status = computed((): OperationStatus | null => {
    if (!opId.value) {
      return null
    }

    return operationStatus(run.value)
  })

  const progress = computed((): OperationProgress | undefined =>
    operationProgress(run.value))

  const output = computed(() => run.value?.output)

  return {
    item,
    run,
    isActive,
    status,
    progress,
    output,
  }
}
